import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { formatDistanceToNow } from 'date-fns'
import { brandName } from '#app/components/branding.js'
import { reactionTypes } from '#app/components/ui/reactions.js'
import { publicProfileCommentFilter } from '#app/utils/comments.server.js'
import { prisma } from '#app/utils/db.server.js'
import { getUserImgSrc } from '#app/utils/misc.js'
import { publicProfileReactionFilter } from '#app/utils/reactions.server.js'

type Activity = {
	type: 'script' | 'build' | 'guide' | 'comment' | 'reaction'
	timestamp: string
	title: string
	link: string
	detail?: string
	reactionEmoji?: string
	/** What was commented on / reacted to — drives the target badge. */
	target?: 'script' | 'build' | 'guide' | 'news' | 'fanSite' | 'builderBuild'
}

const targetBadges: Record<string, { label: string; className: string }> = {
	news: { label: 'News', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' },
	fanSite: { label: 'Fan Site', className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300' },
	builderBuild: { label: 'Builder Build', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300' },
}

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: { id: true, name: true, username: true, image: { select: { id: true } } },
		where: { username: params.username },
	})

	invariantResponse(owner, 'Not found', { status: 404 })

	const [scripts, builds, guides, comments, reactions] = await Promise.all([
		prisma.script.findMany({
			where: { creatorId: owner.id },
			select: { title: true, slug: true, updatedAt: true },
			orderBy: { updatedAt: 'desc' },
			take: 20,
		}),
		prisma.userBuildTemplate.findMany({
			where: { userId: owner.id },
			select: {
				name: true,
				slug: true,
				modifiedAt: true,
				server: { select: { slug: true } },
			},
			orderBy: { modifiedAt: 'desc' },
			take: 20,
		}),
		prisma.guide.findMany({
			where: { creatorId: owner.id },
			select: { title: true, slug: true, updatedAt: true },
			orderBy: { updatedAt: 'desc' },
			take: 20,
		}),
		prisma.comment.findMany({
			where: { userId: owner.id, ...publicProfileCommentFilter },
			select: {
				id: true,
				text: true,
				createdAt: true,
				script: { select: { title: true, slug: true } },
				guide: { select: { title: true, slug: true } },
				build: {
					select: {
						name: true,
						slug: true,
						server: { select: { slug: true } },
					},
				},
				newsPost: { select: { title: true, slug: true } },
				fanSite: { select: { title: true, slug: true } },
				botScriptBuild: { select: { id: true, name: true } },
			},
			orderBy: { createdAt: 'desc' },
			take: 20,
		}),
		prisma.reaction.findMany({
			where: { userId: owner.id, ...publicProfileReactionFilter },
			select: {
				type: true,
				createdAt: true,
				script: { select: { title: true, slug: true } },
				guide: { select: { title: true, slug: true } },
				build: {
					select: {
						name: true,
						slug: true,
						server: { select: { slug: true } },
					},
				},
				newsPost: { select: { title: true, slug: true } },
				fanSite: { select: { title: true, slug: true } },
				comment: {
					select: {
						script: { select: { title: true, slug: true } },
						guide: { select: { title: true, slug: true } },
						build: {
							select: {
								name: true,
								slug: true,
								server: { select: { slug: true } },
							},
						},
						newsPost: { select: { title: true, slug: true } },
						fanSite: { select: { title: true, slug: true, display: true } },
						botScriptBuild: { select: { id: true, name: true, isPublic: true } },
					},
				},
			},
			orderBy: { createdAt: 'desc' },
			take: 20,
		}),
	])

	const activities: Activity[] = []

	for (const s of scripts) {
		activities.push({
			type: 'script',
			timestamp: s.updatedAt.toISOString(),
			title: s.title,
			link: `/script/${s.slug}`,
		})
	}

	for (const b of builds) {
		activities.push({
			type: 'build',
			timestamp: b.modifiedAt.toISOString(),
			title: b.name || 'Unnamed build',
			link: `/builds/${b.server.slug}/${b.slug}`,
		})
	}

	for (const g of guides) {
		activities.push({
			type: 'guide',
			timestamp: g.updatedAt.toISOString(),
			title: g.title,
			link: `/guide/${g.slug}`,
		})
	}

	for (const c of comments) {
		let title = ''
		let link = ''
		let target: Activity['target']
		if (c.script) {
			title = c.script.title
			link = `/script/${c.script.slug}`
			target = 'script'
		} else if (c.guide) {
			title = c.guide.title
			link = `/guide/${c.guide.slug}`
			target = 'guide'
		} else if (c.build) {
			title = c.build.name
			link = `/builds/${c.build.server.slug}/${c.build.slug}`
			target = 'build'
		} else if (c.newsPost) {
			title = c.newsPost.title
			link = `/news/${c.newsPost.slug}`
			target = 'news'
		} else if (c.fanSite) {
			title = c.fanSite.title
			link = `/fan-site/${c.fanSite.slug}`
			target = 'fanSite'
		} else if (c.botScriptBuild) {
			title = c.botScriptBuild.name
			link = `/script-builder/build/${c.botScriptBuild.id}`
			target = 'builderBuild'
		}
		if (link) {
			activities.push({
				type: 'comment',
				timestamp: c.createdAt.toISOString(),
				title,
				link,
				detail: c.text.length > 100 ? c.text.slice(0, 100) + '...' : c.text,
				target,
			})
		}
	}

	for (const r of reactions) {
		let title = ''
		let link = ''
		let target: Activity['target']
		if (r.script) {
			title = r.script.title
			link = `/script/${r.script.slug}`
			target = 'script'
		} else if (r.guide) {
			title = r.guide.title
			link = `/guide/${r.guide.slug}`
			target = 'guide'
		} else if (r.build) {
			title = r.build.name
			link = `/builds/${r.build.server.slug}/${r.build.slug}`
			target = 'build'
		} else if (r.newsPost) {
			title = r.newsPost.title
			link = `/news/${r.newsPost.slug}`
			target = 'news'
		} else if (r.fanSite) {
			title = r.fanSite.title
			link = `/fan-site/${r.fanSite.slug}`
			target = 'fanSite'
		} else if (r.comment) {
			if (r.comment.script) {
				title = r.comment.script.title
				link = `/script/${r.comment.script.slug}`
				target = 'script'
			} else if (r.comment.guide) {
				title = r.comment.guide.title
				link = `/guide/${r.comment.guide.slug}`
				target = 'guide'
			} else if (r.comment.build) {
				title = r.comment.build.name
				link = `/builds/${r.comment.build.server.slug}/${r.comment.build.slug}`
				target = 'build'
			} else if (r.comment.newsPost) {
				title = r.comment.newsPost.title
				link = `/news/${r.comment.newsPost.slug}`
				target = 'news'
			} else if (r.comment.fanSite?.display) {
				// Reacting to a comment on an unapproved fan site must not leak
				// its title here — same rule as publicProfileReactionFilter.
				title = r.comment.fanSite.title
				link = `/fan-site/${r.comment.fanSite.slug}`
				target = 'fanSite'
			} else if (r.comment.botScriptBuild?.isPublic) {
				title = r.comment.botScriptBuild.name
				link = `/script-builder/build/${r.comment.botScriptBuild.id}`
				target = 'builderBuild'
			}
		}
		if (link) {
			const emoji = reactionTypes.find((rt) => rt.type === r.type)?.icon ?? '?'
			activities.push({
				type: 'reaction',
				timestamp: r.createdAt.toISOString(),
				title,
				link,
				reactionEmoji: emoji,
				target,
			})
		}
	}

	activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

	return json({ owner, activities: activities.slice(0, 50) })
}

const typeConfig: Record<Activity['type'], { icon: string; verb: string; className: string }> = {
	script: { icon: '📜', verb: 'updated script', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
	build: { icon: '🛡️', verb: 'updated build', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
	guide: { icon: '📖', verb: 'updated guide', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
	comment: { icon: '💬', verb: 'commented on', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
	reaction: { icon: '⭐', verb: 'reacted to', className: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300' },
}

export default function UserActivityRoute() {
	const { owner, activities } = useLoaderData<typeof loader>()
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Recent Activity</h1>
				</div>
			</div>

			<div className="mt-6"></div>
			<div className="space-y-3">
				{activities.length === 0 && (
					<p className="text-sm text-muted-foreground">No activity yet.</p>
				)}
				{activities.map((activity, index) => {
					const config = typeConfig[activity.type]
					const timeAgo = formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })

					return (
						<div
							key={`${activity.type}-${index}`}
							className="rounded-xl border bg-muted/30 px-4 py-3"
						>
							<div className="flex items-center gap-2 text-sm">
								<span className="text-base leading-none">{config.icon}</span>
								<span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${config.className}`}>
									{config.verb}
								</span>
								{activity.target && targetBadges[activity.target] && (
									<span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${targetBadges[activity.target]!.className}`}>
										{targetBadges[activity.target]!.label}
									</span>
								)}
								<Link to={activity.link} className="font-medium truncate hover:underline">
									{activity.reactionEmoji ? `${activity.reactionEmoji} ` : ''}
									{activity.title}
								</Link>
								<span className="shrink-0 text-xs text-muted-foreground ml-auto">{timeAgo}</span>
							</div>
							{activity.detail && (
								<p className="mt-1.5 whitespace-pre-wrap text-sm text-muted-foreground line-clamp-2 pl-7">
									{activity.detail}
								</p>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	return [
		{ title: `${displayName}'s Activity | ${brandName}` },
		{
			name: 'description',
			content: `${displayName}'s recent activity on ${brandName}`,
		},
	]
}
